import type { ReactNode } from 'react';
import type { Experiment } from 'types';
import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { MoreHorizontal, Copy, Archive, Trash2, Pause } from 'lucide-react';
import { experimentTypeLabel } from 'definitions';
import { cn } from 'helpers/cn';
import { isPreStart } from 'helpers/experiment-status';
import { lacksUsableDestination } from 'helpers/variant-fields';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useExperiment } from '@/state/experiments/useExperiment';
import { useVariants } from '@/state/experiments/useVariants';
import { apiErrorMessage } from '@/state/api-error';
import { useWebsites } from '@/state/useWebsites';
import { WorkspaceToolbarControls } from '../../DashboardLayout/WorkspacePanel';
import { DuplicateWebsiteSelect, useDuplicateTarget } from '../DuplicateWebsiteSelect';
import { QaPreviewPanel } from './QaPreviewPanel';
import { RenameControl } from './RenameControl';
import { StartActions } from './StartActions';

type DialogAction = 'end' | 'duplicate' | 'archive' | 'delete' | null;
type HeaderProps = {
  experiment: Experiment;
  screen: string;
  /** Called once a manual End has gone through. The details screen decides whether to offer the debrief. */
  onEnded: () => Promise<void>;
  /** The results AI's field, at the right of the name row (null on a draft, or while it is off). */
  ask: ReactNode;
};

/** The experiment type is a fact, not a status — neutral chip, never a colour. */
const TYPE_CHIP_CLASS =
  'border-line bg-field text-dim shrink-0 rounded-pill border px-2 py-0.5 font-mono text-nano tracking-[0.1em] uppercase';

const RENAME_COPY = {
  action: 'Rename experiment',
  title: 'Rename Experiment',
  field: 'Experiment Name:',
  done: 'Experiment renamed.',
  failed: 'Could not rename the experiment.',
};

const MENU_ITEM_CLASS = 'text-dim focus:bg-hover focus:text-foreground rounded-md text-meta';

export const Header = ({ experiment, screen, onEnded, ask }: HeaderProps) => {
  const navigate = useNavigate();
  const [dialogAction, setDialogAction] = useState<DialogAction>(null);
  const [duplicateName, setDuplicateName] = useState(experiment.name + ' (copy)');
  const { website } = useWebsites();
  const duplicateTarget = useDuplicateTarget({ experimentWebsiteId: experiment.websiteId });
  const { ganProperty } = website || {};
  const { variants, variantsPending } = useVariants(experiment.id);

  const {
    isStarting,
    enforcing,
    startExperiment,
    startQa,
    stopQa,
    endExperiment,
    duplicateExperiment,
    updateExperiment,
    archiveExperiment,
    deleteExperiment,
    enforceNewRules,
  } = useExperiment(experiment.id);
  const [qaPending, setQaPending] = useState(false);

  const { status, isTest } = experiment;
  // A draft in QA is still a draft: it can be renamed, deleted, switched out of QA or started.
  const isDraft = isPreStart(status);
  const inQa = status === 'QA';
  const isEnded = status === 'Ended';
  const isDeployed = status === 'Deployed';
  const isRunning = status === 'Running';

  const noEditPermissions = isDraft && ganProperty && !ganProperty.hasEditPermission.length;
  const noUrlTargeting = experiment.type === 'URL Redirect' && experiment.urlTargeting.length === 0;
  // Only a draft is held back: a running redirect without one is fixed from its card, not by stopping it.
  const checksDestinations = isDraft && experiment.type === 'URL Redirect';
  const missingDestinations = checksDestinations ? (variants ?? []).filter(lacksUsableDestination) : [];
  // Until the variants arrive there is nothing to check them against, so Start waits too.
  // Pending, not "no data": a failed query would otherwise leave Start disabled for good,
  // and the start endpoint applies the same check anyway.
  const variantsLoading = checksDestinations && variantsPending;
  const missingDestinationNames = missingDestinations.map((variant) => `"${variant.name}"`).join(', ');

  const isStopAction = isRunning || isDeployed;

  /**
   * Every one of the four dialogs below routes its failure through here.
   *
   * Two things were wrong without it. ConfirmDialog disables Confirm while awaiting and
   * re-enables it after, with no `finally` - so a throw left the dialog open with a dead
   * primary button and no way forward but Cancel. And `duplicateExperiment`, `archiveExperiment`
   * and `deleteExperiment` all throw client-side when no website is selected, which the HTTP
   * error dialog never sees, so those three failed in complete silence.
   *
   * The dialog is left open deliberately: for Duplicate the name typed for the copy is still
   * in the field, and for the destructive pair the user should see the action did not happen.
   */
  const runAction = async (action: () => Promise<void>, fallback: string, onDone: () => void | Promise<void>) => {
    try {
      await action();
      await onDone();
    } catch (error) {
      // apiErrorMessage, not instanceof Error: the API client throws a plain object, and
      // a 400 or 404 reaches no global dialog, so this toast is the only report.
      toast.error(apiErrorMessage(error, fallback));
    }
  };

  /**
   * Why nothing can start, in one sentence: neither QA nor the experiment. A preview of a
   * redirect with nowhere to go, or of a page nobody is targeted on, shows nothing useful.
   *
   * Each condition used to leave a greyed-out primary action with nothing to read; only the
   * missing URL rule had a notice on screen explaining itself.
   */
  const blockedReason = noUrlTargeting
    ? 'Configure at least one URL Targeting rule before starting this experiment'
    : missingDestinations.length
      ? `Add a valid destination URL to ${missingDestinationNames} before starting this experiment`
      : variantsLoading
        ? 'Loading the variants…'
        : undefined;
  // Only starting creates the Google Analytics audiences, so only starting needs the permission.
  const startBlockedReason = noEditPermissions
    ? 'You do not have edit permission on the linked Google Analytics property'
    : undefined;

  const toggleQa = async (action: 'start' | 'stop') => {
    setQaPending(true);
    try {
      await (action === 'start' ? startQa : stopQa)(experiment.id);
      toast.success(
        action === 'start'
          ? 'QA is on. Open a preview link below to see it on the live site.'
          : 'QA is off. The experiment is a draft again.',
      );
    } catch (error) {
      toast.error(apiErrorMessage(error, action === 'start' ? 'Could not switch QA on.' : 'Could not switch QA off.'));
    } finally {
      setQaPending(false);
    }
  };

  const PrimaryAction = isDraft ? (
    <StartActions
      inQa={inQa}
      canQa={experiment.type !== 'Server Side'}
      blockedReason={blockedReason}
      startBlockedReason={startBlockedReason}
      isStarting={isStarting}
      qaPending={qaPending}
      onStart={() => startExperiment(experiment.id)}
      onStartQa={() => toggleQa('start')}
      onStopQa={() => toggleQa('stop')}
    />
  ) : (
    <Button
      variant={isStopAction ? 'destructive' : 'default'}
      title={isEnded ? 'This experiment has ended and cannot be started again' : undefined}
      disabled={isEnded}
      onClick={() => setDialogAction('end')}
      className={cn(
        'h-8 min-w-[132px] rounded-lg px-3 text-meta font-medium',
        isStopAction
          ? 'bg-danger-bg border-danger-line text-danger hover:bg-danger-bg border'
          : 'bg-cta hover:bg-ctahover text-white',
      )}
    >
      {isEnded && <span>Ended</span>}
      {isStopAction && (
        <>
          <Pause className="size-3.5" strokeWidth={1.5} />
          End
        </>
      )}
    </Button>
  );

  const MoreMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="More options"
          className="text-dim hover:text-foreground hover:bg-hover size-8 rounded-lg"
        >
          <MoreHorizontal className="size-4" strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-line bg-panel rounded-lg">
        <DropdownMenuItem onClick={() => setDialogAction('duplicate')} className={MENU_ITEM_CLASS}>
          <Copy className="mr-2 size-3.5" strokeWidth={1.5} /> Duplicate
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setDialogAction('archive')} disabled={!isEnded} className={MENU_ITEM_CLASS}>
          <Archive className="mr-2 size-3.5" strokeWidth={1.5} /> Archive
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setDialogAction('delete')}
          disabled={!isDraft}
          className={cn(MENU_ITEM_CLASS, 'text-danger focus:text-danger')}
        >
          <Trash2 className="mr-2 size-3.5" strokeWidth={1.5} /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const EnforceRulesAction =
    isStopAction && screen === 'settings' ? (
      <Button
        disabled={enforcing}
        variant="outline"
        className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta font-medium"
        onClick={enforceNewRules}
      >
        {enforcing ? 'Enforcing...' : 'Enforce new Rules'}
      </Button>
    ) : null;

  /** A name is only editable until the experiment starts, and only from the Settings tab. */
  const RenameAction =
    isDraft && screen === 'settings' ? (
      <RenameControl
        name={experiment.name}
        copy={RENAME_COPY}
        onRename={(name) => updateExperiment(experiment.id, { name })}
      />
    ) : null;

  return (
    <div className="flex flex-col gap-[14px]">
      {/* DESKTOP */}
      <div className="hidden items-start justify-between gap-4 md:flex">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="text-foreground truncate text-[18px] leading-none font-medium" title={experiment.name}>
              {experiment.name}
            </h1>
            {RenameAction}

            <span className={TYPE_CHIP_CLASS}>{experimentTypeLabel(experiment.type)}</span>
          </div>

          <span className="micro-label">id {experiment.id}</span>
        </div>

        {/* Up to 420px, down to 220px on a narrow row, so the experiment's name keeps its room. */}
        {ask && <div className="flex max-w-[420px] min-w-[220px] flex-1 items-center justify-end">{ask}</div>}

        {/* Start / End and the menu sit in the toolbar beside the breadcrumb (decided 2026-10-20),
            which never scrolls, so the name row has room for Ask. Last in the strip, after any
            controls a tab parks there (the Statistics sticky selects). */}
        <WorkspaceToolbarControls>
          <div className="order-last flex shrink-0 items-center gap-2">
            {EnforceRulesAction}
            {PrimaryAction}
            {MoreMenu}
          </div>
        </WorkspaceToolbarControls>
      </div>

      {/* MOBILE */}
      <div className="flex flex-col gap-3 md:hidden">
        <div className="flex items-center justify-end">{MoreMenu}</div>

        <div className="flex items-center justify-between gap-3">
          {/* min-w-0, or `truncate` has nothing to shrink against and the type chip
              beside it gets pushed off the right edge instead. */}
          <h1 className="text-foreground min-w-0 truncate text-title leading-tight font-medium" title={experiment.name}>
            {experiment.name}
          </h1>
          {RenameAction}
          <span className={cn(TYPE_CHIP_CLASS, 'shrink-0')}>{experimentTypeLabel(experiment.type)}</span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="micro-label">id {experiment.id}</span>

          <div className="flex shrink-0 items-center gap-2">
            {EnforceRulesAction}
            {PrimaryAction}
          </div>
        </div>

        {/* The toolbar is desktop only, so on a phone the actions stay above and Ask takes a row. */}
        {ask && <div className="flex">{ask}</div>}
      </div>

      {(noUrlTargeting || missingDestinations.length > 0) && (
        <section className="bg-panel border-warn-line rounded-panel border p-4">
          <p className="micro-label text-warn">url redirect incomplete</p>
          <p className="supporting mt-1">This experiment cannot be started with the current settings.</p>
          <ul className="supporting mt-1 list-disc pl-5">
            {noUrlTargeting && <li>Configure at least one rule for URL Targeting.</li>}
            {missingDestinations.length > 0 && (
              <li>
                Add a valid destination URL to {missingDestinationNames}, so visitors in{' '}
                {missingDestinations.length === 1 ? 'that variant' : 'those variants'} have somewhere to go.
              </li>
            )}
          </ul>
        </section>
      )}

      {inQa && (
        <QaPreviewPanel
          experimentId={experiment.id}
          qaExpiresAt={experiment.qaExpiresAt}
          variants={variants ?? []}
          defaultPageUrl={experiment.editorUrl || website?.url || ''}
        />
      )}

      <ConfirmDialog
        title="End Experiment?"
        open={dialogAction === 'end'}
        onConfirm={() =>
          runAction(
            () => endExperiment(experiment.id),
            'Could not end the experiment.',
            async () => {
              setDialogAction(null);
              // The page stays put, so nothing on screen would otherwise confirm it worked.
              toast.success('Experiment ended.');
              // Only a manual end from here asks. Scheduled, pipeline and API ends have no
              // screen to prompt on, so those rely on the attention queue instead. The prompt
              // is optional and closing it changes nothing: the experiment has already ended.
              await onEnded();
            },
          )
        }
        onCancel={() => setDialogAction(null)}
        confirmLabel="End"
        confirmVariant="destructive"
      >
        Are you sure you want to end this experiment?
      </ConfirmDialog>

      <ConfirmDialog
        title="Duplicate Experiment"
        open={dialogAction === 'duplicate'}
        onConfirm={() =>
          runAction(
            () => duplicateExperiment(experiment.id, duplicateName, duplicateTarget.targetWebsiteId),
            'Could not duplicate the experiment.',
            () => {
              setDialogAction(null);
              // The library shows the website selected in the header, so a copy filed
              // elsewhere would otherwise appear to have vanished.
              if (duplicateTarget.isMove)
                toast.success(`Copied to ${duplicateTarget.targetUrl ?? 'the other website'}.`);
              navigate('/experiments/library');
            },
          )
        }
        onCancel={() => setDialogAction(null)}
        confirmLabel="Duplicate"
        disabled={!duplicateName.trim()}
      >
        <Label className="micro-label flex flex-col gap-2">
          New Experiment Name:
          <Input
            value={duplicateName}
            onChange={(ev) => setDuplicateName(ev.target.value)}
            className="border-line bg-field text-foreground rounded-lg"
          />
          {/* ConfirmDialog takes no reason for its `disabled`, so the reason is stated here
              instead: clearing the field greyed Duplicate out with nothing to read. */}
          {!duplicateName.trim() && <span className="supporting">Enter a name for the copy to continue.</span>}
        </Label>
        <div className="mt-4">
          <DuplicateWebsiteSelect
            id={`duplicate-website-${experiment.id}`}
            value={duplicateTarget.targetWebsiteId}
            onChange={duplicateTarget.setTargetWebsiteId}
          />
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        title="Archive Experiment"
        open={dialogAction === 'archive'}
        onConfirm={async () => {
          await archiveExperiment(experiment.id);
          setDialogAction(null);
          navigate('/experiments/library');
        }}
        onCancel={() => setDialogAction(null)}
        confirmLabel="Archive"
      >
        Are you sure you want to archive this experiment? Archived experiments can be viewed but not modified.
      </ConfirmDialog>

      <ConfirmDialog
        title="Delete Experiment"
        open={dialogAction === 'delete'}
        onConfirm={async () => {
          await deleteExperiment(experiment.id);
          setDialogAction(null);
          navigate('/experiments/library');
        }}
        onCancel={() => setDialogAction(null)}
        confirmLabel="Delete"
        confirmVariant="destructive"
      >
        Are you sure you want to delete this experiment? This action cannot be undone.
      </ConfirmDialog>
    </div>
  );
};
